// Import Required libraries
import React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { Grid, Card, Box, IconButton, Typography } from "@mui/material";
// Import Custom Components
import SigviewTypography from "../../components/Common/SigviewTypography";
import SigviewSwitch from "../../components/Common/SigviewSwitch";
import { ReportNameChange } from "./ReportHelpers";
// Import Actions
import { updateDialogInfo, updateUserScreen } from "../../redux/actions";
import { masterTrackGaEvent } from "../../services/ga";
// Imports Images
import renameIcon from "../../../assets/images/renameIcon.svg";
import deleteIcon from "../../../assets/images/deleteIcon.svg";
import editIcon from "../../../assets/images/editIcon.svg";
// Import Config
import { config } from "../../config/config";
// Import API's
import {
  deleteSavedReport,
  deleteScheduledReport,
  updateScheduledReport,
} from "../../services/api";
import SigviewTooltip from "../../components/Common/SigviewTooltip";

const ReportCardRow = (props) => {
  const {
    id,
    dispatch: ReduxDispatcher,
    report,
    reportType,
    user,
    onToggle,
    onClick,
    variant = "report",
    activeReport,
    setActiveReport,
    reloadData,
    isActive = false,
    payload,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [reportActive, setReportActive] = useState({
    status: "success",
    message: "",
    data: isActive,
  });
  const reportMessages = config.messages.reports;
  const handleGAButtonClick = (label) => {
    // Google Analytics Event - Master
    masterTrackGaEvent({
      category: "ReportManager",
      action: "ButtonClicked",
      label,
    });
  };
  const handleDeleteSavedReport = () => {
    handleGAButtonClick("DeleteSaved");
    //Set is loading to true
    setIsLoading(true);

    //Update snackbar
    let snackbarPayload = {
      ...user.screen.snackbar,
      open: true,
      message: reportMessages.reportDeleteRequest,
    };
    ReduxDispatcher(updateUserScreen("snackbar", snackbarPayload));

    //Making fetch call
    const reportId = report.id;
    const fetchProps = {
      url: `${user.apiEndpoints.baseUrl}${config.apiEndpoints.deleteSavedReport}/${reportId}`,
    };
    const deleteSavedReportPromise = deleteSavedReport(fetchProps);
    deleteSavedReportPromise
      .then((responseDate) => {
        //Update snackbar
        let snackbarPayload = {
          ...user.screen.snackbar,
          open: true,
          message: reportMessages.reportDeleteSuccess,
        };
        ReduxDispatcher(updateUserScreen("snackbar", snackbarPayload));
        if (variant === "adminReport") {
          ReduxDispatcher(updateUserScreen("adminReport", false));
        }

        if (variant === "report") {
          //Close the active report
          setActiveReport({ type: "none", id: "" });

          //Reload data
          reloadData("saved");
        }
      })
      .catch((json) => {
        console.log("API FAILED", json);
        setIsLoading(false);
        let snackbarPayload = {
          ...user.screen.snackbar,
          open: true,
          message: json.error || reportMessages.reportDeleteFailed,
        };
        ReduxDispatcher(updateUserScreen("snackbar", snackbarPayload));
      });
  };
  const handleDeleteScheduledReport = () => {
    handleGAButtonClick("DeleteScheduled");

    //Set is loading to true
    setIsLoading(true);

    //Update snackbar
    let snackbarPayload = {
      ...user.screen.snackbar,
      open: true,
      message: reportMessages.reportDeleteRequest,
    };
    ReduxDispatcher(updateUserScreen("snackbar", snackbarPayload));

    //Making fetch call
    const reportId = report.id;
    const fetchProps = {
      url: `${user.apiEndpoints.baseUrl}${config.apiEndpoints.deleteScheduledReport}/${reportId}`,
    };
    const deleteScheduledReportPromise = deleteScheduledReport(fetchProps);
    deleteScheduledReportPromise
      .then((responseDate) => {
        //Update snackbar
        let snackbarPayload = {
          ...user.screen.snackbar,
          open: true,
          message: reportMessages.reportDeleteSuccess,
        };
        ReduxDispatcher(updateUserScreen("snackbar", snackbarPayload));

        if (variant === "adminReport") {
          ReduxDispatcher(updateUserScreen("adminReport", false));
        }

        if (variant === "report") {
          //Close the active report
          setActiveReport({ type: "none", id: "" });

          //Reload data
          reloadData("scheduled");
        }
      })
      .catch((json) => {
        console.log("API FAILED", json);
        setIsLoading(false);
        let snackbarPayload = {
          ...user.screen.snackbar,
          open: true,
          message: json.error || reportMessages.reportDeleteFailed,
        };
        ReduxDispatcher(updateUserScreen("snackbar", snackbarPayload));
      });
  };
  const handleDeleteReportDialog = (reportType) => {
    const message = {
      title: reportMessages.reportDeleteConfirmation,
      subtitle: "",
    };
    const handleNo = () => {
      //Close the dialog box
      ReduxDispatcher(
        updateDialogInfo({
          ...user.dialogInfo,
          open: false,
        })
      );
    };
    const handleYes = () => {
      //Close the dialog box
      ReduxDispatcher(
        updateDialogInfo({
          ...user.dialogInfo,
          open: false,
          message: { title: "", subtitle: "" },
        })
      );
      if (reportType === "saved") {
        handleDeleteSavedReport();
      } else if (reportType === "scheduled") {
        handleDeleteScheduledReport();
      }
    };
    ReduxDispatcher(
      updateDialogInfo({
        ...user.dialogInfo,
        open: true,
        message,
        handleYes,
        handleNo,
      })
    );
  };
  const onToggleSwitch = (event, checked) => {
    //To prevent the report from being clicked
    event.stopPropagation();
    // Google Analytics Event - Master
    masterTrackGaEvent({
      category: "ReportManager",
      action: "ScheduledToggle",
      label: checked ? "On" : "Off",
    });
    //Set status to loading
    setReportActive((prevState) => ({ ...prevState, status: "loading" }));
    //Make a fetch call to update status
    const fetchPayload = {
      ...payload,
      reportStatus: checked ? "active" : "inactive",
      statusToggleTime: new Date().valueOf().toString(),
    };
    const fetchProps = {
      payload: fetchPayload,
    };
    const updateScheduledReportPromise = updateScheduledReport(fetchProps);
    updateScheduledReportPromise
      .then(() => {
        //Update local state
        setReportActive((prevState) => ({
          ...prevState,
          status: "success",
          data: checked,
        }));
        //Update parent scheduled reports
        onToggle(id, fetchPayload);
      })
      .catch((error) => {
        if (error.error !== config.hardCoded.queryCancelled) {
          setReportActive((prevState) => ({ ...prevState, status: "success" }));
        }
      });
  };
  const handleRenameDialog = () => {
    const initialName = report.title;
    const payload = {
      open: true,
      children: (
        <ReportNameChange
          ReduxDispatcher={ReduxDispatcher}
          initialName={initialName}
          report={report.payload}
          reportType={reportType}
          user={user}
          setActiveReport={setActiveReport}
          reloadData={reloadData}
        />
      ),
    };
    const action = updateUserScreen("sigviewDialog", payload);
    ReduxDispatcher(action);
  };
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card
        variant="outlined"
        sx={{
          p: 0,
          borderRadius: "6px",
          overflow: "hidden",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
          cursor: "pointer",
          transition: "0.2s",
          "&:hover": {
            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
          },
        }}
        onClick={() => onClick?.({ type: reportType, id: report.id })}
      >
        {/* Top Section - Time */}
        <Box
          display="flex"
          px={2}
          py={1.5}
          sx={{ backgroundColor: "#fff" }}
          gap={4}
          alignItems="center"
          height= "24px"
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {report.subtitle}
          </Typography>
          {reportActive.status === "success" &&
            reportType === "scheduled" && ( //Not showing for the one that is active; user can change it from the form panel
              // !isCurrentlySelected &&
              <SigviewSwitch
                checked={reportActive.data}
                onChange={onToggleSwitch}
                customClassName="scheduleReportON-OFF-GA"
              />
            )}
        </Box>

        {/* Bottom Section - Report Name + Icons */}
        <Box px={2} py={1.5} sx={{ backgroundColor: "#F8F9FD" }}>
          <SigviewTypography
            variant="pLarge"
            style={{
              color: "#2E3C5D",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block", // ensure it's block to take full width
              maxWidth: "100%", // prevent overflow
              cursor: "pointer",
            }}
          >
            {report.title}
          </SigviewTypography>

          {reportType !== "download" && (
            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              gap={2}
              mt={1}
            >
              <SigviewTooltip title="Rename">
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRenameDialog();
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <img src={renameIcon} alt="Rename" />
                </Box>
              </SigviewTooltip>
              <SigviewTooltip title="Delete">
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteReportDialog(reportType);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <img src={deleteIcon} alt="Delete" />
                </Box>
              </SigviewTooltip>
              <SigviewTooltip title="Edit">
                <Box
                  onClick={(e) => {
                    // e.stopPropagation();
                    //   handleShare(workbook, worksheet);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <img src={editIcon} alt="Edit" />
                </Box>
              </SigviewTooltip>
            </Box>
          )}
        </Box>
      </Card>
    </Grid>
  );
};
const mapStateToProps = (state) => ({
  user: state.user,
  allData: state.data,
  globalFilters: state.globalFilters,
});

export default connect(mapStateToProps)(ReportCardRow);
